import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { LogOut, Download, ArrowLeft, ChevronLeft, ChevronRight, RefreshCw, Calendar as CalendarIcon } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#CCFF00', '#7000FF', '#FF0099', '#00CCFF', '#FF9900', '#00FF66', '#FF6600', '#9900FF'];

const PERIODS = [
  { value: 'today', label: 'Сегодня' },
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'all', label: 'Всё время' },
  { value: 'custom', label: 'Выбрать даты' },
];

function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [showCalendar, setShowCalendar] = useState(false);

  const fetchAnalytics = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setRefreshing(true);
    try {
      const params = { period, page, per_page: 15 };
      if (period === 'custom' && dateRange.from && dateRange.to) {
        params.start_date = format(dateRange.from, 'yyyy-MM-dd');
        params.end_date = format(dateRange.to, 'yyyy-MM-dd');
      }
      const response = await axios.get(`${API}/admin/analytics`, { params });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Ошибка загрузки аналитики");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, page, dateRange]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchAnalytics();
  }, [navigate, fetchAnalytics]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setPage(1);
    if (newPeriod === 'custom') {
      setShowCalendar(true);
    } else {
      setShowCalendar(false);
      setDateRange({ from: null, to: null });
    }
  };

  const handleDateSelect = (range) => {
    setDateRange(range || { from: null, to: null });
  };

  const applyDateRange = () => {
    if (dateRange.from && dateRange.to) {
      setShowCalendar(false);
      fetchAnalytics();
    } else {
      toast.error("Выберите обе даты");
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const periodLabels = { 'today': 'Today', 'week': 'This Week', 'month': 'This Month', 'year': 'This Year', 'all': 'All Time' };
    const periodLabel = periodLabels[period] || 'All Time';
    let currentY = 0;
    
    // === PAGE 1: HEADER & SUMMARY ===
    
    // Dark header banner
    doc.setFillColor(20, 20, 20);
    doc.rect(0, 0, pageWidth, 50, 'F');
    
    // Accent line
    doc.setFillColor(0, 150, 80);
    doc.rect(0, 50, pageWidth, 3, 'F');
    
    // Logo text
    doc.setTextColor(0, 200, 100);
    doc.setFontSize(32);
    doc.setFont('helvetica', 'bold');
    doc.text("SKINKOSH", 15, 28);
    
    // Subtitle
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text("CRYPTO EXCHANGE", 15, 40);
    
    // Report info on right
    doc.setTextColor(180, 180, 180);
    doc.setFontSize(11);
    doc.text("ANALYTICS REPORT", pageWidth - 15, 22, { align: 'right' });
    doc.setFontSize(10);
    doc.text(`Period: ${periodLabel}`, pageWidth - 15, 32, { align: 'right' });
    doc.text(`Generated: ${new Date().toLocaleString('en-US')}`, pageWidth - 15, 42, { align: 'right' });
    
    currentY = 65;
    
    // === EXECUTIVE SUMMARY BOX ===
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(15, currentY, pageWidth - 30, 45, 3, 3, 'F');
    
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text("EXECUTIVE SUMMARY", 20, currentY + 10);
    
    // Summary stats in columns
    const summaryY = currentY + 22;
    const colWidth = (pageWidth - 40) / 4;
    
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 100, 0); // Dark green - readable on white
    doc.text(String(stats?.total_exchanges || 0), 25, summaryY);
    doc.text(`$${((stats?.completed_volume_usd || 0) / 1000).toFixed(1)}K`, 25 + colWidth, summaryY);
    doc.text(`$${(stats?.total_profit || 0).toFixed(0)}`, 25 + colWidth * 2, summaryY);
    doc.text(`${stats?.conversion_rate || 0}%`, 25 + colWidth * 3, summaryY);
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text("Total Orders", 25, summaryY + 8);
    doc.text("Volume (USD)", 25 + colWidth, summaryY + 8);
    doc.text("Profit (USD)", 25 + colWidth * 2, summaryY + 8);
    doc.text("Conversion", 25 + colWidth * 3, summaryY + 8);
    
    currentY = currentY + 55;
    
    // === ORDER STATISTICS ===
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text("ORDER STATISTICS", 15, currentY);
    
    autoTable(doc, {
      startY: currentY + 5,
      margin: { left: 15, right: 15 },
      theme: 'plain',
      styles: { fontSize: 11, cellPadding: 4 },
      headStyles: { fillColor: [30, 30, 30], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: { 
        0: { cellWidth: 80, fontStyle: 'bold' }, 
        1: { cellWidth: 50, halign: 'right' },
        2: { cellWidth: 50, halign: 'right' }
      },
      head: [['Status', 'Count', 'Percentage']],
      body: [
        ['Completed', stats?.completed_exchanges || 0, `${stats?.total_exchanges ? ((stats?.completed_exchanges / stats?.total_exchanges) * 100).toFixed(1) : 0}%`],
        ['Pending', stats?.pending_exchanges || 0, `${stats?.total_exchanges ? ((stats?.pending_exchanges / stats?.total_exchanges) * 100).toFixed(1) : 0}%`],
        ['Cancelled', stats?.cancelled_exchanges || 0, `${stats?.total_exchanges ? ((stats?.cancelled_exchanges / stats?.total_exchanges) * 100).toFixed(1) : 0}%`],
        ['TOTAL', stats?.total_exchanges || 0, '100%'],
      ],
      didDrawPage: (data) => { currentY = data.cursor.y; }
    });
    
    currentY += 15;
    
    // === FINANCIAL METRICS ===
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text("FINANCIAL METRICS", 15, currentY);
    
    autoTable(doc, {
      startY: currentY + 5,
      margin: { left: 15, right: 15 },
      theme: 'plain',
      styles: { fontSize: 11, cellPadding: 4 },
      headStyles: { fillColor: [112, 0, 255], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: { 
        0: { cellWidth: 100, fontStyle: 'bold' }, 
        1: { halign: 'right', fontStyle: 'bold' }
      },
      head: [['Metric', 'Value (USD)']],
      body: [
        ['Total Volume', `$ ${(stats?.total_volume_usd || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}`],
        ['Completed Volume', `$ ${(stats?.completed_volume_usd || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}`],
        ['Total Profit', `$ ${(stats?.total_profit || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}`],
        ['Average Order Value', `$ ${(stats?.avg_order_value || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}`],
        ['Unique Clients', stats?.unique_users || 0],
        ['Commission Rate', `${stats?.commission_percent || 0}%`],
      ],
      didDrawPage: (data) => { currentY = data.cursor.y; }
    });
    
    // === PAGE 2: TOP PAIRS & DAILY ===
    doc.addPage();
    currentY = 20;
    
    // Top pairs section
    if (stats?.top_pairs && stats.top_pairs.length > 0) {
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text("TOP TRADING PAIRS", 15, currentY);
      
      // Helper to shorten currency names
      const shortenCurrency = (name) => {
        const map = {
          'tether-trc20': 'USDT(TRC)',
          'tether-erc20': 'USDT(ERC)',
          'tether': 'USDT',
          'ethereum': 'ETH',
          'bitcoin': 'BTC',
          'litecoin': 'LTC',
          'binancecoin': 'BNB',
          'ripple': 'XRP',
          'cardano': 'ADA',
          'solana': 'SOL',
          'dogecoin': 'DOGE'
        };
        const lower = name.toLowerCase();
        return map[lower] || name.substring(0, 6).toUpperCase();
      };
      
      const formatPair = (pair) => {
        const parts = pair.split(' -> ');
        if (parts.length === 2) {
          return `${shortenCurrency(parts[0])} > ${shortenCurrency(parts[1])}`;
        }
        return pair.substring(0, 20);
      };
      
      autoTable(doc, {
        startY: currentY + 5,
        margin: { left: 15, right: 15 },
        theme: 'striped',
        styles: { fontSize: 10, cellPadding: 4 },
        headStyles: { fillColor: [50, 50, 50], textColor: [255, 255, 255], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center' },
          1: { cellWidth: 50 },
          2: { cellWidth: 22, halign: 'center' },
          3: { cellWidth: 22, halign: 'center' },
          4: { cellWidth: 40, halign: 'right' },
          5: { cellWidth: 22, halign: 'center' }
        },
        head: [['#', 'From > To', 'Ord', 'Done', 'Volume $', 'Conv']],
        body: stats.top_pairs.slice(0, 10).map((p, i) => [
          i + 1,
          formatPair(p.pair), 
          p.count, 
          p.completed,
          `$${Number(p.volume_usd).toLocaleString()}`, 
          `${p.conversion_rate}%`
        ]),
        didDrawPage: (data) => { currentY = data.cursor.y; }
      });
      
      currentY += 15;
    }
    
    // Daily activity
    if (stats?.daily_activity && stats.daily_activity.length > 0) {
      if (currentY > 180) {
        doc.addPage();
        currentY = 20;
      }
      
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text("DAILY ACTIVITY BREAKDOWN", 15, currentY);
      
      autoTable(doc, {
        startY: currentY + 5,
        margin: { left: 15, right: 15 },
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [50, 50, 50], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
        head: [['Date', 'Orders', 'Done', 'Pend', 'Canc', 'Volume USD', 'Profit USD', 'Clients']],
        body: stats.daily_activity.slice(0, 20).map(d => [
          d.date, 
          d.count, 
          d.completed,
          d.pending,
          d.cancelled,
          `$ ${Number(d.volume_usd).toLocaleString()}`, 
          `$ ${d.profit}`,
          d.unique_users
        ]),
        didDrawPage: (data) => { currentY = data.cursor.y; }
      });
    }
    
    // === FOOTER ON ALL PAGES ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Footer line
      doc.setDrawColor(200, 200, 200);
      doc.line(15, pageHeight - 15, pageWidth - 15, pageHeight - 15);
      
      // Footer text
      doc.setFontSize(8);
      doc.setTextColor(130, 130, 130);
      doc.text("SkinKosh Crypto Exchange - Confidential Report", 15, pageHeight - 8);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 15, pageHeight - 8, { align: 'right' });
    }
    
    doc.save(`SkinKosh-Analytics-${periodLabel.replace(' ', '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF Report exported");
  };

  const exportToCSV = () => {
    if (!stats) return;
    
    let csv = "=== SUMMARY ===\n";
    csv += "Metric,Value\n";
    csv += `Period,${PERIODS.find(p => p.value === period)?.label}\n`;
    csv += `Total Exchanges,${stats.total_exchanges}\n`;
    csv += `Completed,${stats.completed_exchanges}\n`;
    csv += `Pending,${stats.pending_exchanges}\n`;
    csv += `Unique Users,${stats.unique_users}\n`;
    csv += `Conversion Rate,${stats.conversion_rate}%\n`;
    csv += `Total Volume USD,$${stats.total_volume_usd?.toFixed(2)}\n`;
    csv += `Total Profit,$${stats.total_profit?.toFixed(2)}\n`;
    csv += `Avg Order Value,$${stats.avg_order_value?.toFixed(2)}\n\n`;
    
    csv += "=== TOP EXCHANGE PAIRS ===\n";
    csv += "Pair,Count,Completed,Volume USD,Conversion Rate\n";
    stats.top_pairs?.forEach(p => {
      csv += `${p.pair},${p.count},${p.completed},${p.volume_usd},${p.conversion_rate}%\n`;
    });
    
    csv += "\n=== DAILY ACTIVITY ===\n";
    csv += "Date,Requests,Completed,Pending,Cancelled,Volume USD,Profit,Unique Users,Avg Order\n";
    stats.daily_activity?.forEach(d => {
      csv += `${d.date},${d.count},${d.completed},${d.pending},${d.cancelled},${d.volume_usd},${d.profit},${d.unique_users},${d.avg_order}\n`;
    });
    
    csv += "\n=== FROM CURRENCIES ===\n";
    csv += "Currency,Count,Volume,Volume USD\n";
    stats.by_from_currency?.forEach(c => {
      csv += `${c.currency},${c.count},${c.volume},${c.volume_usd}\n`;
    });
    
    csv += "\n=== TO CURRENCIES ===\n";
    csv += "Currency,Count,Volume,Volume USD\n";
    stats.by_to_currency?.forEach(c => {
      csv += `${c.currency},${c.count},${c.volume},${c.volume_usd}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skinkosh-analytics-${period}-${Date.now()}.csv`;
    a.click();
    toast.success("CSV экспортирован");
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary font-heading text-2xl">Загрузка...</div>
      </div>
    );
  }

  const pagination = stats?.pagination || {};

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/admin/dashboard")}
              variant="ghost"
              size="icon"
              className="hover:bg-primary/10 hover:text-primary"
            >
              <ArrowLeft size={24} />
            </Button>
            <h1 className="font-heading text-2xl font-bold text-primary tracking-tight">
              АНАЛИТИКА
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => fetchAnalytics(false)}
              variant="ghost"
              size="icon"
              className={`hover:bg-primary/10 ${refreshing ? 'animate-spin' : ''}`}
              disabled={refreshing}
            >
              <RefreshCw size={20} />
            </Button>
            <Button
              onClick={exportToCSV}
              variant="outline"
              size="sm"
              className="border-primary text-primary"
            >
              <Download size={14} className="mr-1" />
              CSV
            </Button>
            <Button
              onClick={exportToPDF}
              variant="outline"
              size="sm"
              className="border-primary text-primary"
            >
              <Download size={14} className="mr-1" />
              PDF
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-destructive text-destructive"
            >
              <LogOut size={14} className="mr-1" />
              Выйти
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {/* Period Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {PERIODS.map((p) => (
            <Button
              key={p.value}
              onClick={() => handlePeriodChange(p.value)}
              variant={period === p.value ? "default" : "outline"}
              size="sm"
              className={period === p.value 
                ? "bg-primary text-primary-foreground" 
                : "border-border hover:border-primary hover:text-primary"
              }
            >
              {p.value === 'custom' && <CalendarIcon className="w-4 h-4 mr-1" />}
              {p.label}
            </Button>
          ))}
          
          {/* Custom Date Range Picker */}
          {period === 'custom' && (
            <Popover open={showCalendar} onOpenChange={setShowCalendar}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="ml-2 border-primary text-primary">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {dateRange.from && dateRange.to ? (
                    <>
                      {format(dateRange.from, 'dd.MM.yyyy', { locale: ru })} - {format(dateRange.to, 'dd.MM.yyyy', { locale: ru })}
                    </>
                  ) : (
                    'Выберите период'
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-card border-border" align="start">
                <div className="p-4">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={handleDateSelect}
                    numberOfMonths={2}
                    locale={ru}
                    className="rounded-md"
                    defaultMonth={new Date(2026, 1, 1)}
                    fromYear={2020}
                    toYear={2030}
                    captionLayout="dropdown-buttons"
                  />
                  <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-border">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setDateRange({ from: null, to: null });
                        setShowCalendar(false);
                        setPeriod('all');
                      }}
                    >
                      Отмена
                    </Button>
                    <Button 
                      size="sm"
                      onClick={applyDateRange}
                      disabled={!dateRange.from || !dateRange.to}
                    >
                      Применить
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}
          
          <span className="ml-auto text-sm text-muted-foreground self-center">
            Комиссия: {stats?.commission_percent}%
          </span>
        </div>

        {/* Main Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total & Completed */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 shadow-lg">
            <div className="flex justify-between items-start mb-3">
              <div className="text-slate-400 text-sm">Всего заявок</div>
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <span className="text-blue-400 text-lg">📊</span>
              </div>
            </div>
            <div className="font-heading text-3xl font-bold text-white mb-1">
              {stats?.total_exchanges || 0}
            </div>
            <div className="flex gap-3 text-xs">
              <span className="text-green-400">✓ {stats?.completed_exchanges || 0}</span>
              <span className="text-yellow-400">⏳ {stats?.pending_exchanges || 0}</span>
              <span className="text-red-400">✕ {stats?.cancelled_exchanges || 0}</span>
            </div>
          </div>
          
          {/* Volume */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 shadow-lg">
            <div className="flex justify-between items-start mb-3">
              <div className="text-slate-400 text-sm">Объём (завершённый)</div>
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <span className="text-emerald-400 text-lg">💰</span>
              </div>
            </div>
            <div className="font-heading text-3xl font-bold text-emerald-400 mb-1">
              ${(stats?.completed_volume_usd || 0).toLocaleString()}
            </div>
            <div className="text-xs text-slate-400">
              Всего: ${(stats?.total_volume_usd || 0).toLocaleString()}
            </div>
          </div>
          
          {/* Profit */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 shadow-lg">
            <div className="flex justify-between items-start mb-3">
              <div className="text-slate-400 text-sm">Прибыль</div>
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <span className="text-amber-400 text-lg">📈</span>
              </div>
            </div>
            <div className="font-heading text-3xl font-bold text-amber-400 mb-1">
              ${(stats?.total_profit || 0).toFixed(2)}
            </div>
            <div className="text-xs text-slate-400">
              Комиссия: {stats?.commission_percent || 0}%
            </div>
          </div>
          
          {/* Conversion & Users */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 shadow-lg">
            <div className="flex justify-between items-start mb-3">
              <div className="text-slate-400 text-sm">Конверсия</div>
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <span className="text-purple-400 text-lg">🎯</span>
              </div>
            </div>
            <div className="font-heading text-3xl font-bold text-purple-400 mb-1">
              {stats?.conversion_rate || 0}%
            </div>
            <div className="text-xs text-slate-400">
              👤 {stats?.unique_users || 0} клиентов • Ср. чек ${(stats?.avg_order_value || 0).toFixed(0)}
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Top Exchange Pairs */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 shadow-lg">
            <h3 className="font-heading font-bold text-lg mb-4 uppercase text-white">Топ валютных пар</h3>
            {stats?.top_pairs && stats.top_pairs.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={stats.top_pairs.slice(0, 6)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis type="number" stroke="#888" />
                    <YAxis type="category" dataKey="pair" stroke="#888" width={120} fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }}
                      formatter={(value, name) => {
                        if (name === 'volume_usd') return [`$${value}`, 'Объём'];
                        if (name === 'count') return [value, 'Заявок'];
                        return [value, name];
                      }}
                    />
                    <Bar dataKey="count" fill="#CCFF00" name="count" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-600">
                        <th className="text-left py-2 text-slate-400">Пара</th>
                        <th className="text-right py-2 text-slate-400">Заявок</th>
                        <th className="text-right py-2 text-slate-400">Завершено</th>
                        <th className="text-right py-2 text-slate-400">Объём</th>
                        <th className="text-right py-2 text-slate-400">Конв.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_pairs.map((pair) => (
                        <tr key={pair.pair} className="border-b border-slate-600/30">
                          <td className="py-1.5 font-mono text-xs text-slate-200">{pair.pair}</td>
                          <td className="py-1.5 text-right text-white">{pair.count}</td>
                          <td className="py-1.5 text-right text-green-400">{pair.completed}</td>
                          <td className="py-1.5 text-right text-slate-200">${pair.volume_usd}</td>
                          <td className="py-1.5 text-right text-blue-400">{pair.conversion_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Нет данных
              </div>
            )}
          </div>

          {/* Currency Distribution */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 shadow-lg">
            <h3 className="font-heading font-bold text-lg mb-4 uppercase text-white">Распределение по валютам</h3>
            {stats?.by_from_currency && stats.by_from_currency.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats.by_from_currency.slice(0, 8)}
                    dataKey="count"
                    nameKey="currency"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ currency, percent }) => `${currency} ${(percent * 100).toFixed(0)}%`}
                  >
                    {stats.by_from_currency.slice(0, 8).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }}
                    formatter={(value, name, props) => [
                      `${value} заявок ($${props.payload.volume_usd})`,
                      props.payload.currency
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Нет данных
              </div>
            )}
          </div>
        </div>

        {/* Hourly Distribution */}
        {stats?.hourly_distribution && stats.hourly_distribution.some(h => h.count > 0) && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6 shadow-lg">
            <h3 className="font-heading font-bold text-lg mb-4 uppercase text-white">Активность по часам</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={stats.hourly_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="hour" stroke="#888" fontSize={10} />
                <YAxis stroke="#888" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }}
                  formatter={(value) => [`${value} заявок`, 'Активность']}
                />
                <Area type="monotone" dataKey="count" stroke="#CCFF00" fill="#CCFF00" fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Daily Activity with Pagination */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6 shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-heading font-bold text-lg uppercase text-white">Активность по дням</h3>
            {pagination.total_pages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  className="border-border"
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {page} / {pagination.total_pages}
                </span>
                <Button
                  onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.total_pages}
                  className="border-border"
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            )}
          </div>
          
          {stats?.daily_activity && stats.daily_activity.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={[...stats.daily_activity].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" stroke="#888" fontSize={10} />
                  <YAxis stroke="#888" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#111', border: '1px solid #333' }}
                    formatter={(value, name) => {
                      const labels = {
                        count: 'Заявок',
                        completed: 'Завершено',
                        profit: 'Прибыль'
                      };
                      if (name === 'profit') return [`$${value}`, labels[name]];
                      return [value, labels[name] || name];
                    }}
                  />
                  <Legend 
                    formatter={(value) => {
                      const labels = { count: 'Заявок', completed: 'Завершено' };
                      return labels[value] || value;
                    }}
                  />
                  <Bar dataKey="count" fill="#CCFF00" name="count" />
                  <Bar dataKey="completed" fill="#00FF00" name="completed" />
                </BarChart>
              </ResponsiveContainer>
              
              {/* Daily table */}
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-600">
                      <th className="text-left py-2 px-2 text-slate-400">Дата</th>
                      <th className="text-right py-2 px-2 text-slate-400">Заявок</th>
                      <th className="text-right py-2 px-2 text-slate-400">Завершено</th>
                      <th className="text-right py-2 px-2 text-slate-400">Ожидание</th>
                      <th className="text-right py-2 px-2 text-slate-400">Отмена</th>
                      <th className="text-right py-2 px-2 text-slate-400">Объём</th>
                      <th className="text-right py-2 px-2 text-slate-400">Прибыль</th>
                      <th className="text-right py-2 px-2 text-slate-400">Клиенты</th>
                      <th className="text-right py-2 px-2 text-slate-400">Ср. чек</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.daily_activity.map((day) => (
                      <tr key={day.date} className="border-b border-slate-600/50 hover:bg-slate-700/50">
                        <td className="py-2 px-2 font-mono text-slate-200">{day.date}</td>
                        <td className="py-2 px-2 text-right font-bold text-white">{day.count}</td>
                        <td className="py-2 px-2 text-right text-green-400">{day.completed}</td>
                        <td className="py-2 px-2 text-right text-yellow-400">{day.pending}</td>
                        <td className="py-2 px-2 text-right text-red-400">{day.cancelled}</td>
                        <td className="py-2 px-2 text-right font-mono text-slate-200">${day.volume_usd.toLocaleString()}</td>
                        <td className="py-2 px-2 text-right font-mono text-emerald-400">${day.profit.toFixed(2)}</td>
                        <td className="py-2 px-2 text-right text-slate-300">{day.unique_users}</td>
                        <td className="py-2 px-2 text-right font-mono text-slate-300">${day.avg_order}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-muted-foreground">
              Нет данных за выбранный период
            </div>
          )}
        </div>

        {/* Status Breakdown */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 shadow-lg">
          <h3 className="font-heading font-bold text-lg mb-4 uppercase text-white">По статусам</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {stats?.by_status?.map((item) => (
              <div key={item.status} className="bg-slate-700/50 border border-slate-600 rounded-lg p-3 text-center">
                <div className="text-slate-400 text-xs mb-1">{item.status}</div>
                <div className="font-heading text-xl font-bold text-white">{item.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboard;
